// ===========================================================================
// Filtros homologados de solicitudes (venta_financiamiento)
// ===========================================================================
// Un solo lugar para:
//   - el rango de fechas por default (mes actual) y sus atajos
//   - los catálogos fijos (tipo de pago, departamento, status, sub status)
//   - las opciones de los combos (Asesor, Financiera, Fuente, Agencia), que
//     se arman con las solicitudes que ya se cargaron (sin consultas extra)
//   - armar el payload para /solicitudes/consultar
//   - la barra de filtros que usan Solicitudes, Resumen, Ranking y
//     Rendimiento F&I (el dashboard tiene sus propios filtros)
//
// Si cambia una agencia, un status o un filtro, se cambia aquí y se refleja
// en todos los módulos.
// ===========================================================================

import React, { useEffect, useMemo, useState } from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import Moment from "moment";
import "bootstrap-daterangepicker/daterangepicker.css";
import "moment/locale/es";

Moment.locale("es");

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------
export const API_BASE = "https://api.checkmark.mx/";
export const ACCESS_TOKEN =
  "1MhYphhrGUbgbdRy7cyYFGQlXS7tAi6ooUoptkHQbOm9GcsKiZM7uMF1MERxwMWq30I7Y3";
export const API_SOLICITUDES = `${API_BASE}solicitudes/consultar`;

const leerJsonStorage = (key, fallback) => {
  try {
    const value = JSON.parse(localStorage.getItem(key));
    return value ?? fallback;
  } catch (error) {
    return fallback;
  }
};

// Datos de sesión que todos los módulos ya mandaban a la API
export const getSesionUsuario = () => ({
  modulos: leerJsonStorage("modulos", []),
  usuario_id: localStorage.getItem("userId"),
  agencias: leerJsonStorage("agencias", []),
});

// ---------------------------------------------------------------------------
// Rango de fechas
// ---------------------------------------------------------------------------
// Todos los módulos arrancan con el mes actual (del día 1 a hoy), así solo
// se consulta lo del mes. La búsqueda por cliente/folio ignora el rango para
// poder encontrar registros viejos.
export const ETIQUETA_RANGO_DEFAULT = "mes actual";

export const getRangoDefault = () => ({
  startDate: Moment().startOf("month").format("YYYY-MM-DD"),
  endDate: Moment().format("YYYY-MM-DD"),
});

export const getPresetsRango = () => ({
  "Mes actual": [Moment().startOf("month"), Moment()],
  "Últimos 7 días": [Moment().subtract(6, "days"), Moment()],
  "Mes anterior": [
    Moment().subtract(1, "month").startOf("month"),
    Moment().subtract(1, "month").endOf("month"),
  ],
  "Últimos 90 días": [Moment().subtract(89, "days"), Moment()],
  "Este año": [Moment().startOf("year"), Moment()],
  "Año anterior": [
    Moment().subtract(1, "year").startOf("year"),
    Moment().subtract(1, "year").endOf("year"),
  ],
});

// ---------------------------------------------------------------------------
// Catálogos fijos
// ---------------------------------------------------------------------------
export const TIPOS_PAGO = ["Crédito", "Contado", "Arrendamiento"];
export const DEPARTAMENTOS = ["Nuevos", "Seminuevos", "(En blanco)"];

export const STATUS_POR_TIPO_PAGO = {
  Crédito: ["Sin status", "Autorizado", "Rechazado", "Pendiente de Buro"],
  Contado: ["Sin status", "Cotización", "Formalizado"],
  Arrendamiento: [
    "Sin status",
    "Cotización",
    "Pendiente de Autorización",
    "Autorizado",
    "Rechazado",
    "Formalizado",
  ],
};

export const STATUS_TODOS = [
  "Sin status",
  "Autorizado",
  "Rechazado",
  "Pendiente de Buro",
  "Cotización",
  "Pendiente de Autorización",
  "Formalizado",
];

// Sub status: solo aplica a crédito y depende del status (mismo catálogo
// que subStatusOptionsCredito en update.js)
export const SUBSTATUS_POR_STATUS_CREDITO = {
  "Sin status": ["Sin substatus"],
  Autorizado: [
    "Condicionado Viable",
    "Condicionado No Viable",
    "Facturado",
    "Formalizado",
    "Aprobado Sin Formalizar",
  ],
  Rechazado: ["Rechazado"],
  "Pendiente de Buro": ["Análisis", "Incompleto"],
};

// Todos los sub status de crédito (cuando no hay status elegido)
export const SUBSTATUS_CREDITO = [
  "Sin substatus",
  "Análisis",
  "Incompleto",
  "Condicionado Viable",
  "Condicionado No Viable",
  "Aprobado Sin Formalizar",
  "Facturado",
  "Formalizado",
  "Rechazado",
];

export const SI_NO = ["Si", "No"];

// Financieras: catálogo fijo (el mismo que se usa para asignar la financiera
// en Solicitudes). Antes las opciones del filtro salían de lo capturado y se
// colaban textos que se escribieron a mano. "value" es la clave técnica /
// columna en venta_financiamiento; "label" es lo que se muestra y se filtra.
// FinanMadrid ahora se llama Finsus (la columna sigue siendo finanmadrid).
export const FINANCIERAS = [
  { label: "NR Finance", value: "nr_finance" },
  { label: "Banorte", value: "banorte" },
  { label: "Scotiabank", value: "scotiabank" },
  { label: "Santander", value: "santander" },
  { label: "Cibanco", value: "cibanco" },
  { label: "Sicrea", value: "sicrea" },
  { label: "BBVA", value: "bbva" },
  { label: "Inbursa", value: "inbursa" },
  { label: "Hey Banco", value: "hey_banco" },
  { label: "HSBC", value: "hsbc" },
  { label: "Cetelem", value: "cetelem" },
  { label: "VWSF", value: "vwfs" },
  { label: "Seat SF", value: "seat_sf" },
  { label: "Cupra SF", value: "cupra_sf" },
  { label: "Kuna", value: "kuna" },
  { label: "Mstar", value: "mstar" },
  { label: "Credito GO", value: "credito_go" },
  { label: "Finsus", value: "finanmadrid" },
  { label: "Nexu", value: "nexu" },
  { label: "TipAuto", value: "tip_auto" },
];

// ---------------------------------------------------------------------------
// Estado de filtros
// ---------------------------------------------------------------------------
// "" = Todos
export const crearFiltrosIniciales = () => ({
  ...getRangoDefault(),
  agencia: "",
  departamento: "",
  tipoPago: "",
  asesor: "",
  financiera: "",
  status: "",
  subStatus: "",
  fuente: "",
  multicarma: "",
  busqueda: "",
});

const CLAVES_SECUNDARIAS = ["subStatus", "fuente", "multicarma"];

// ---------------------------------------------------------------------------
// Payload para /solicitudes/consultar
// ---------------------------------------------------------------------------
// Regresa { payload, sinResultados }.
export const construirPayloadSolicitudes = (filters, extra = {}) => {
  const sesion = getSesionUsuario();
  const agencias = Array.isArray(sesion.agencias) ? sesion.agencias : [];
  const sinResultados = false;

  const busqueda = String(filters.busqueda || "").trim();

  const payload = {
    access_token: ACCESS_TOKEN,
    modulos: sesion.modulos,
    usuario_id: sesion.usuario_id,
    agencias,
    // La búsqueda por cliente/folio busca en todo el historial
    startDate: busqueda ? "" : filters.startDate,
    endDate: busqueda ? "" : filters.endDate,
    agencia: filters.agencia,
    asesor: filters.asesor,
    financiera: filters.financiera,
    status: filters.status,
    sub_status: filters.subStatus,
    tipo_pago: filters.tipoPago,
    tipo_auto: filters.departamento,
    fuente: filters.fuente,
    multicarma: filters.multicarma,
    busqueda,
    ...extra,
  };

  return { payload, sinResultados };
};

// Consulta /solicitudes/consultar con los filtros homologados.
// Regresa { statusCode, resultados } (resultados siempre es arreglo).
export const consultarSolicitudes = async (filters, extra = {}) => {
  const { payload, sinResultados } = construirPayloadSolicitudes(
    filters,
    extra,
  );

  if (sinResultados) return { statusCode: 404, resultados: [], data: null };

  const response = await fetch(API_SOLICITUDES, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const data = await response.json();
  const resultados = Array.isArray(data?.data?.resultados)
    ? data.data.resultados
    : [];

  return { statusCode: data?.statusCode, resultados, data };
};

// ---------------------------------------------------------------------------
// Opciones de los combos
// ---------------------------------------------------------------------------
// Se arman con las solicitudes que el módulo ya cargó (no hay consultas
// extra). Se van acumulando mientras el módulo está abierto, para que al
// aplicar un filtro no desaparezcan las demás opciones del combo.
const textoLimpio = (value) => String(value ?? "").trim();

export function useOpcionesFiltros(resultados = []) {
  const [opciones, setOpciones] = useState({
    asesor: [],
    financiera: [],
    fuente: [],
    agencias: [],
  });

  useEffect(() => {
    if (!Array.isArray(resultados) || !resultados.length) return;

    setOpciones((prev) => {
      const asesor = new Set(prev.asesor);
      const financiera = new Set(prev.financiera);
      const fuente = new Set(prev.fuente);
      const agencias = new Map(prev.agencias.map((a) => [a.agencia, a]));

      resultados.forEach((item) => {
        const a = textoLimpio(item?.asesor);
        const f = textoLimpio(item?.financiera);
        const fu = textoLimpio(item?.fuente);
        const ag = textoLimpio(item?.agencia);
        if (a) asesor.add(a);
        if (f) financiera.add(f);
        if (fu) fuente.add(fu);
        if (ag && !agencias.has(ag)) {
          agencias.set(ag, { agencia: ag, ag_id: textoLimpio(item?.ag_id) });
        }
      });

      return {
        asesor: Array.from(asesor),
        financiera: Array.from(financiera),
        fuente: Array.from(fuente),
        agencias: Array.from(agencias.values()),
      };
    });
  }, [resultados]);

  return { opciones };
}

// ---------------------------------------------------------------------------
// Opciones dinámicas (cada combo se acota con lo que ya está elegido)
// ---------------------------------------------------------------------------
const unicos = (lista) =>
  Array.from(new Set(lista.filter(Boolean))).sort((a, b) =>
    String(a).localeCompare(String(b), "es", { sensitivity: "base" }),
  );

const conValorActual = (lista, actual) =>
  actual && !lista.includes(actual) ? [...lista, actual] : lista;

// Sub status disponibles según el tipo de pago y el status elegidos:
//  - Contado / Arrendamiento: no manejan sub status → []
//  - Con status de crédito elegido: solo los sub status de ese status
//  - Status que no es de crédito (ej. Cotización): []
//  - Sin status elegido: todos los de crédito
export const opcionesSubStatus = (filters) => {
  if (filters.tipoPago && filters.tipoPago !== "Crédito") return [];
  if (filters.status) return SUBSTATUS_POR_STATUS_CREDITO[filters.status] || [];
  return SUBSTATUS_CREDITO;
};

export const calcularOpcionesDinamicas = (filters, opciones) => {
  const nombresAgencia = unicos(
    (Array.isArray(opciones?.agencias) ? opciones.agencias : []).map((a) =>
      String(a.agencia || "").trim(),
    ),
  );

  const status = filters.tipoPago
    ? STATUS_POR_TIPO_PAGO[filters.tipoPago] || STATUS_TODOS
    : STATUS_TODOS;

  // Catálogo fijo, ordenado alfabéticamente para el combo
  const financieras = unicos(FINANCIERAS.map((f) => f.label));
  const fuentes = unicos(["Digital", ...(opciones?.fuente || [])]);

  return {
    agencia: conValorActual(nombresAgencia, filters.agencia),
    asesor: conValorActual(unicos(opciones?.asesor || []), filters.asesor),
    financiera: conValorActual(financieras, filters.financiera),
    status: conValorActual(status, filters.status),
    subStatus: opcionesSubStatus(filters),
    fuente: conValorActual(fuentes, filters.fuente),
  };
};

export const contarFiltrosActivos = (filters) => {
  const base = crearFiltrosIniciales();
  return Object.keys(base).filter((key) => {
    if (key === "startDate" || key === "endDate") return false;
    return Boolean(filters[key]);
  }).length;
};

// ---------------------------------------------------------------------------
// Barra de filtros
// ---------------------------------------------------------------------------
// Props:
//   filters, onChange(nuevosFiltros)
//   opciones: resultado de useOpcionesFiltros().opciones
//   mostrar: claves visibles (por default todas)
//   titulo (opcional), children (botones extra a la derecha, ej. reporte)
const ETIQUETAS = {
  fecha: "Fecha",
  agencia: "Agencia",
  departamento: "Departamento",
  tipoPago: "Tipo de pago",
  asesor: "Asesor",
  financiera: "Financiera",
  status: "Status",
  subStatus: "Sub status",
  fuente: "Fuente",
  multicarma: "Multicarma",
  busqueda: "Buscar cliente / folio",
};

export const FILTROS_TODOS = Object.keys(ETIQUETAS);

// Estado de "Más filtros" mientras la página esté abierta (solo memoria)
let masFiltrosRecordado = false;

export function BarraFiltrosSolicitudes({
  filters,
  onChange,
  opciones,
  mostrar = FILTROS_TODOS,
  children = null,
}) {
  const visibles = new Set(mostrar);
  const secundariasVisibles = CLAVES_SECUNDARIAS.filter((k) => visibles.has(k));
  const hayActivasSecundarias = secundariasVisibles.some((k) => filters[k]);

  // Se recuerda abierto/cerrado aunque el módulo oculte la barra mientras
  // carga (si no, se cerraba en cada cambio de filtro)
  const [masFiltros, setMasFiltrosState] = useState(
    masFiltrosRecordado || hayActivasSecundarias,
  );
  const setMasFiltros = (updater) => {
    setMasFiltrosState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      masFiltrosRecordado = next;
      return next;
    });
  };
  const [textoBusqueda, setTextoBusqueda] = useState(filters.busqueda || "");

  useEffect(() => {
    setTextoBusqueda(filters.busqueda || "");
  }, [filters.busqueda]);

  const dinamicas = useMemo(
    () => calcularOpcionesDinamicas(filters, opciones),
    [filters, opciones],
  );

  const rangoDefault = getRangoDefault();
  const esRangoDefault =
    filters.startDate === rangoDefault.startDate &&
    filters.endDate === rangoDefault.endDate;

  // Cambia un filtro y limpia los que dependen de él si ya no son válidos
  const cambiar = (key, value) => {
    const next = { ...filters, [key]: value };

    if (key === "tipoPago") {
      const validos = value ? STATUS_POR_TIPO_PAGO[value] || [] : STATUS_TODOS;
      if (next.status && !validos.includes(next.status)) next.status = "";
    }

    // Si el sub status elegido ya no pertenece al tipo de pago / status, se
    // limpia
    if (
      (key === "tipoPago" || key === "status") &&
      next.subStatus &&
      !opcionesSubStatus(next).includes(next.subStatus)
    ) {
      next.subStatus = "";
    }

    onChange(next);
  };

  const aplicarBusqueda = () => {
    const valor = textoBusqueda.trim();
    if (valor !== (filters.busqueda || "")) cambiar("busqueda", valor);
  };

  const limpiar = () => {
    setTextoBusqueda("");
    onChange(crearFiltrosIniciales());
  };

  const activos = contarFiltrosActivos(filters);
  const col = "col-12 col-sm-6 col-md-4 col-xl-2";

  const select = (key, lista, { todos = "Todos", disabled = false } = {}) =>
    visibles.has(key) ? (
      <div className={col} key={key}>
        <label className="form-label fw-semibold black-text mb-1">
          {ETIQUETAS[key]}
        </label>
        <select
          className="form-select form-select-sm"
          value={filters[key] || ""}
          disabled={disabled}
          onChange={(e) => cambiar(key, e.target.value)}
        >
          <option value="">{todos}</option>
          {lista.map((op) => (
            <option key={op} value={op}>
              {op}
            </option>
          ))}
        </select>
      </div>
    ) : null;

  const subStatusDeshabilitado = opcionesSubStatus(filters).length === 0;

  return (
    <div className="card mb-3">
      <div className="card-body py-3">
        <div className="row g-2 align-items-end">
          {visibles.has("fecha") ? (
            <div className="col-12 col-md-8 col-xl-3">
              <label className="form-label fw-semibold black-text mb-1">
                {ETIQUETAS.fecha}
                {filters.busqueda ? (
                  <span className="ms-2 small text-muted fw-normal">
                    (la búsqueda ignora el rango)
                  </span>
                ) : esRangoDefault ? (
                  <span className="ms-2 small text-muted fw-normal">
                    ({ETIQUETA_RANGO_DEFAULT})
                  </span>
                ) : null}
              </label>
              <div className="d-flex gap-2">
                <div className="flex-grow-1">
                  <DateRangePicker
                    key={`${filters.startDate}|${filters.endDate}`}
                    startDate={Moment(filters.startDate || rangoDefault.startDate, "YYYY-MM-DD")}
                    endDate={Moment(filters.endDate || rangoDefault.endDate, "YYYY-MM-DD")}
                    ranges={getPresetsRango()}
                    locale={{
                      format: "YYYY-MM-DD",
                      applyLabel: "Aplicar",
                      cancelLabel: "Cancelar",
                      customRangeLabel: "Rango personalizado",
                    }}
                    onApply={(event, picker) =>
                      onChange({
                        ...filters,
                        startDate: picker.startDate.format("YYYY-MM-DD"),
                        endDate: picker.endDate.format("YYYY-MM-DD"),
                      })
                    }
                  >
                    <button
                      type="button"
                      className="form-control form-control-sm text-start"
                    >
                      <i className="fa-regular fa-calendar me-2"></i>
                      {filters.startDate && filters.endDate
                        ? `${filters.startDate} | ${filters.endDate}`
                        : "Selecciona un rango"}
                    </button>
                  </DateRangePicker>
                </div>
                {!esRangoDefault ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    title={`Regresar al ${ETIQUETA_RANGO_DEFAULT}`}
                    onClick={() => onChange({ ...filters, ...getRangoDefault() })}
                  >
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {visibles.has("busqueda") ? (
            <div className="col-12 col-md-4 col-xl-3">
              <label className="form-label fw-semibold black-text mb-1">
                {ETIQUETAS.busqueda}
              </label>
              <div style={{ display: "flex", gap: 6 }}>
                <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: 9,
                      top: "50%",
                      transform: "translateY(-50%)",
                      opacity: 0.55,
                      pointerEvents: "none",
                    }}
                  >
                    <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.2" />
                    <line x1="15.5" y1="15.5" x2="21" y2="21" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                  </svg>
                  <input
                    type="search"
                    className="form-control form-control-sm"
                    style={{ paddingLeft: 28 }}
                    placeholder="Nombre, teléfono, correo o folio"
                    value={textoBusqueda}
                    onChange={(e) => {
                      setTextoBusqueda(e.target.value);
                      if (!e.target.value && filters.busqueda) {
                        cambiar("busqueda", "");
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") aplicarBusqueda();
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  style={{ whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 5 }}
                  onClick={aplicarBusqueda}
                  title="Buscar (también puedes presionar Enter)"
                >
                  <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
                    <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.4" />
                    <line x1="15.5" y1="15.5" x2="21" y2="21" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
                  </svg>
                  Buscar
                </button>
              </div>
            </div>
          ) : null}

          {select("agencia", dinamicas.agencia, { todos: "Todas" })}
          {select("departamento", DEPARTAMENTOS)}
          {select("tipoPago", TIPOS_PAGO)}
          {select("asesor", dinamicas.asesor)}
          {select("financiera", dinamicas.financiera, { todos: "Todas" })}
          {select("status", dinamicas.status)}

          {masFiltros ? (
            <>
              {select("subStatus", dinamicas.subStatus, {
                disabled: subStatusDeshabilitado,
              })}
              {select("fuente", dinamicas.fuente, { todos: "Todas" })}
              {select("multicarma", SI_NO)}
            </>
          ) : null}

          <div className="col-12 d-flex flex-wrap align-items-center gap-2 mt-2">
            {secundariasVisibles.length ? (
              <button
                type="button"
                className="btn btn-sm btn-link px-0"
                onClick={() => setMasFiltros((v) => !v)}
              >
                <i
                  className={`fa-solid ${masFiltros ? "fa-chevron-up" : "fa-chevron-down"} me-1`}
                ></i>
                {masFiltros ? "Menos filtros" : "Más filtros"}
                {!masFiltros && hayActivasSecundarias ? " (activos)" : ""}
              </button>
            ) : null}

            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={limpiar}
              disabled={!activos && esRangoDefault}
            >
              Limpiar filtros{activos ? ` (${activos})` : ""}
            </button>

            {children ? <div className="ms-auto d-flex gap-2">{children}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
}